import React from 'react';
import I18n from 'i18n-js';

import { Listing } from '@reverbdotcom/commons/src/gql/graphql';
import CoreLink from '@reverbdotcom/commons/src/components/core_link';
import { listingOrdersPath, listingOffersPath } from '@reverbdotcom/commons/src/url_helpers';
import { RCTextWithIcon } from '@reverbdotcom/cadence/components';
import { EyeIcon, HeartStraightIcon } from '@reverbdotcom/cadence/icons/react';
import { useEppoExpEnabled } from '@reverbdotcom/commons/src/user_hooks';
import eppoFlags from '@reverbdotcom/commons/src/eppoFlags.json';

const DRAFT = 'draft';

interface IExternalProps {
  listing: Listing;
}

export default function SellerListingStats({
  listing,
}: IExternalProps) {
  const isFavoritesRebrandEnabled = useEppoExpEnabled({ flagKey: eppoFlags.SAVED_SEARCH_FAVORITES_REBRAND });

  if (listing.state == DRAFT) return null;

  const ordersCount = listing.orderStats?.totalOrders ?? 0;
  const offersCount = listing.counts.offers;
  const inBuyerCartsCount = listing.counts.inBuyerCarts;
  const watchersOrSaves = isFavoritesRebrandEnabled ? I18n.t('discovery.listingsManagement.sellerListingCard.saves', {
    count: listing.counts.watchers,
  }) : I18n.t('discovery.listingsManagement.sellerListingCard.watchers', {
    count: listing.counts.watchers,
  });

  return (
    <ul
      className="d-flex fx-align-center gap-4 pr-4 weight-semibold"
      style={{ fontSize: 'var(--rc-font-size-350)' }}
    >
      <li className="tablet-d-none">
        {I18n.t('discovery.listingsManagement.sellerListingCard.views', {
          count: listing.counts.views,
        })}
      </li>
      <li className="d-none tablet-d-inline">
        <RCTextWithIcon
          icon={<EyeIcon />}
          placement="left"
        >
          {listing.counts.views}
        </RCTextWithIcon>
      </li>

      <li className="tablet-d-none">
        {watchersOrSaves}
      </li>
      <li className="d-none tablet-d-inline">
        <RCTextWithIcon
          icon={<HeartStraightIcon />}
          placement="left"
        >
          {listing.counts.watchers}
        </RCTextWithIcon>
      </li>

      {offersCount > 0 && (
        <li className="tablet-d-none">
          <CoreLink
            to={listingOffersPath(listing)}
            className="td-underline fg-color-text-primary"
          >
            {I18n.t('discovery.listingsManagement.sellerListingCard.offers', {
              count: offersCount,
            })}
          </CoreLink>
        </li>
      )}

      {inBuyerCartsCount > 0 && (
        <li className="tablet-d-none">
          {I18n.t('discovery.listingsManagement.sellerListingCard.inBuyerCarts', {
            count: listing.counts.inBuyerCarts,
          })}
        </li>
      )}

      {ordersCount > 0 && (
        <li className="tablet-d-none">
          <CoreLink
            to={listingOrdersPath(listing)}
            className="td-underline fg-color-text-primary"
          >
            {I18n.t('discovery.listingsManagement.sellerListingCard.orders', {
              count: ordersCount,
            })}
          </CoreLink>
        </li>
      )}

      {!!listing.bumpRate?.rate && (
        <li className="tablet-d-none">
          {I18n.t('discovery.listingsManagement.sellerListingCard.bumped', {
            percent: listing.bumpRate.rate,
          })}
        </li>
      )}
    </ul>
  );
}
